import { AnimationData, AnimatorManager, IAnimations } from 'feature-animations'
import type { MenuContainerData } from './types'

interface IAnimationHandler {
	animate(
		menuContainerId: string,
		menuContainerAnimations: MenuContainerData['animations'],
		isOpen: boolean,
		onComplete: (reversed: boolean) => void
	): Promise<void>
}

type AnimationHandlerFactory = (animations: IAnimations) => IAnimationHandler
type MenuAnimationsInstances = { containerAnimation?: any; overlayAnimation?: any }

const DEFAULT_ANIMATIONS: { [propertyName: string]: AnimationData } = {
	containerOut: {
		targetId: '',
		name: 'FadeOut',
	},
	baseClear: {
		targetId: '',
		name: 'BaseClear',
		params: {
			props: 'clip,clipPath,webkitClipPath,opacity,transform,transformOrigin',
			immediateRender: false,
		},
		delay: 0,
		duration: 0,
	},
	overlayIn: {
		targetId: '',
		name: 'FadeIn',
	},
	overlayOut: {
		targetId: '',
		name: 'FadeOut',
	},
}

export const AnimationHandler: AnimationHandlerFactory = (animations) => {
	const animationsInstances: { [menuContainerId: string]: MenuAnimationsInstances } = {}

	const getContainerAnimationToExecute = (
		menuContainerId: string,
		menuContainerAnimations: MenuContainerData['animations'],
		isOpen: boolean,
		onCompleteCb: (reversed: boolean) => void
	) => {
		const inBehavior = menuContainerAnimations.inBehavior
		const outBehavior = menuContainerAnimations.outBehavior || DEFAULT_ANIMATIONS.containerOut

		const animationData = isOpen ? inBehavior : { ...inBehavior, name: outBehavior.name }
		animationData.targetId = `container-${menuContainerId}`

		animationData.params = {
			...animationData.params,
			callbacks: {
				onComplete: onCompleteCb.bind(null, false),
				onReverseComplete: onCompleteCb.bind(null, true),
			},
		}

		return animationData as AnimationData
	}

	const getOverlayAnimationToExecute = (
		menuContainerId: string,
		isOpen: boolean,
		containerAnimationData: AnimationData
	) => {
		const animationData = isOpen ? { ...DEFAULT_ANIMATIONS.overlayIn } : { ...DEFAULT_ANIMATIONS.overlayOut }

		animationData.targetId = `overlay-${menuContainerId}`
		animationData.duration = containerAnimationData.duration
		animationData.delay = containerAnimationData.delay

		const cb = () => {
			delete animationsInstances[menuContainerId].overlayAnimation
		}
		animationData.params = {
			...animationData.params,
			callbacks: {
				onComplete: cb,
				onReverseComplete: cb,
			},
		}

		return animationData
	}

	const animateElement = (
		animatorManager: AnimatorManager,
		animationInstance: MenuAnimationsInstances['containerAnimation' | 'overlayAnimation'],
		animationData: AnimationData
	) => {
		if (animationData.duration && animationData.duration > 0 && animationInstance) {
			animatorManager.reverse(animationInstance)
			return animationInstance
		} else {
			return animatorManager.runAnimation(animationData)
		}
	}

	const onCompleteAnimation = (animatorManager: AnimatorManager, menuContainerId: string, isOpen: boolean) => {
		const containerAnimationData = { ...DEFAULT_ANIMATIONS.baseClear, targetId: `container-${menuContainerId}` }
		const overlayAnimationData = getOverlayAnimationToExecute(menuContainerId, isOpen, containerAnimationData)

		const cb = () => {
			delete animationsInstances[menuContainerId].containerAnimation
		}
		containerAnimationData.params = {
			...containerAnimationData.params,
			callbacks: {
				onComplete: cb,
				onReverseComplete: cb,
			},
		}
		animationsInstances[menuContainerId] = animationsInstances[menuContainerId] || {}
		handleAnimations(animatorManager, menuContainerId, containerAnimationData, overlayAnimationData)
	}

	const handleAnimations = (
		animatorManager: AnimatorManager,
		menuContainerId: string,
		containerAnimationData: AnimationData,
		overlayAnimationData: AnimationData
	) => {
		const menuAnimationsInstances = animationsInstances[menuContainerId]
		menuAnimationsInstances.containerAnimation = animateElement(
			animatorManager,
			menuAnimationsInstances.containerAnimation,
			containerAnimationData
		)
		menuAnimationsInstances.overlayAnimation = animateElement(
			animatorManager,
			menuAnimationsInstances.overlayAnimation,
			overlayAnimationData
		)
	}

	return {
		async animate(menuContainerId, menuContainerAnimations, isOpen, onComplete) {
			const animatorManager = await animations.getInstance()

			const onCompleteCb = (reversed: boolean) => {
				animationsInstances[menuContainerId] && delete animationsInstances[menuContainerId].containerAnimation
				onComplete(reversed)
				onCompleteAnimation(animatorManager, menuContainerId, isOpen)
			}

			if (menuContainerAnimations.inBehavior) {
				const containerAnimationData = getContainerAnimationToExecute(
					menuContainerId,
					menuContainerAnimations,
					isOpen,
					onCompleteCb
				)
				const overlayAnimationData = getOverlayAnimationToExecute(
					menuContainerId,
					isOpen,
					containerAnimationData
				)

				animationsInstances[menuContainerId] = animationsInstances[menuContainerId] || {}
				handleAnimations(animatorManager, menuContainerId, containerAnimationData, overlayAnimationData)
			} else {
				onCompleteCb(false)
			}
		},
	}
}
